import {createSignal, JSX} from 'solid-js';
import appMediaPlaybackController, {AppMediaPlaybackController} from '@components/appMediaPlaybackController';
import cancelEvent from '@helpers/dom/cancelEvent';
import {attachClickEvent} from '@helpers/dom/clickEvent';
import PeerTitle from '@components/peerTitle';
import I18n, {i18n} from '@lib/langPack';
import {formatFullSentTime} from '@helpers/date';
import getAudioTitles from '@appManagers/utils/docs/getAudioTitles';
import MediaProgressLine from '@components/mediaProgressLine';
import VolumeSelector from '@components/volumeSelector';
import wrapEmojiText from '@lib/richTextProcessor/wrapEmojiText';
import {AppManagers} from '@lib/managers';
import getFwdFromName from '@appManagers/utils/messages/getFwdFromName';
import toHHMMSS from '@helpers/string/toHHMMSS';
import {PlaybackRateButton} from '@components/playbackRateButton';
import createAudioAnimatedIcon, {createPlayPauseIcon} from '@components/audioAnimatedIcon';
import apiManagerProxy from '@lib/apiManagerProxy';
import {doubleRaf} from '@helpers/schedulers';
import ListenerSetter from '@helpers/listenerSetter';
import SetTransition from '@components/singleTransition';
import {ChatType} from './chatType';
import type {AppImManager} from '@lib/appImManager';
import findUpClassName from '@helpers/dom/findUpClassName';
import toggleDisability from '@helpers/dom/toggleDisability';
import appSidebarRight from '../sidebarRight';
import {openSavedMusicTab} from '@components/savedMusicActions';
import TopbarPlate, {createTopbarPlate} from '@components/chat/topbarPlate';
import Button from '@components/buttonTsx';
import documentFragmentToNodes from '@helpers/dom/documentFragmentToNodes';
import classNames from '@helpers/string/classNames';

export type ChatAudioController = {
  container: HTMLElement,
  destroy: () => void
};

export default function createChatAudio(
  appImManager: AppImManager,
  managers: AppManagers
): ChatAudioController {
  const listenerSetter = new ListenerSetter();
  let duration: number;

  // ───────────────────────── Reactive state ─────────────────────────

  const [title, setTitle] = createSignal<JSX.Element>();
  const [subtitle, setSubtitle] = createSignal<JSX.Element>();
  const [timeText, setTimeText] = createSignal('');
  // the seek glyphs rest on their finished frame and replay on every press
  const rewindIcon = createAudioAnimatedIcon('rewind');
  const forwardIcon = createAudioAnimatedIcon('forward');
  let playIconContainer!: HTMLDivElement;
  const setPlayIcon = createPlayPauseIcon(() => playIconContainer);

  // The controller announces a play one task late (see its `onPlay`), so a press that lands inside
  // that gap is told about a play that is already over, and the glyph sits out the change. The media
  // itself is never late, so the plate follows it directly — the same rule the row's button follows.
  const mediaListenerSetter = new ListenerSetter();
  let playingMedia: HTMLMediaElement;
  const [playing, setPlaying] = createSignal(false);
  const syncPlayIcon = () => {
    const isPlaying = !!playingMedia && !playingMedia.paused;
    setPlaying(isPlaying);
    setPlayIcon(isPlaying);
  };

  const followMedia = (media: HTMLMediaElement) => {
    if(playingMedia !== media) {
      mediaListenerSetter.removeAll();
      playingMedia = media;
      (['play', 'pause', 'emptied'] as const).forEach((event) => {
        mediaListenerSetter.add(media)(event, syncPlayIcon);
      });
    }

    syncPlayIcon();
  };

  const [repeatIcon, setRepeatIcon] = createSignal<Icon>('audio_repeat');

  // A track picked in the music search popup plays off a message that only ever existed inside that
  // popup — there is nothing to open, so the plate's content stops being a button altogether.
  const [inert, setInert] = createSignal(false);

  // Refs to JSX-rendered buttons that need imperative classList toggles or
  // disability state changes after the initial render.
  let prevEl!: HTMLElement;
  let nextEl!: HTMLElement;
  let repeatEl!: HTMLElement;

  // ───────────────────────── Imperative widgets ─────────────────────────

  const playbackRateButton = PlaybackRateButton({direction: 'bottom-left'});

  const volumeSelector = new VolumeSelector({listenerSetter, vertical: true, useGlobalVolume: 'auto'});
  const volumeProgressLineContainer = document.createElement('div');
  volumeProgressLineContainer.classList.add('progress-line-container');
  volumeProgressLineContainer.append(volumeSelector.container);
  const tunnel = document.createElement('div');
  tunnel.classList.add('pinned-audio-volume-tunnel');
  volumeSelector.btn.classList.add('pinned-audio-volume');
  volumeSelector.btn.prepend(tunnel);
  volumeSelector.btn.append(volumeProgressLineContainer);

  const progressLine = new MediaProgressLine({
    withTransition: true,
    useTransform: true,
    onTimeUpdate: (t) => setTimeText(toHHMMSS(t, true))
  });
  progressLine.container.classList.add('pinned-audio-progress');

  // ───────────────────────── Plate ─────────────────────────

  // The audio plate uses its own visibility logic (`is-visible` + `body.is-pinned-audio-shown`)
  // wired below in `toggle()`. The default `hide` class would conflict with the SCSS
  // `:not(.is-visible) { display: none !important }` rule, so we keep the plate root unmarked.
  const plate = createTopbarPlate({
    modifier: 'audio',
    height: 48,
    initiallyHidden: false,
    render: () => (
      <>
        <TopbarPlate.Body noRipple>
          <Button
            ref={prevEl}
            class="btn-icon"
            noRipple
            aria-label={I18n.format('KeyboardShortcuts.Action.PreviousMedia', true)}
            onClick={(e) => { cancelEvent(e); rewindIcon.play(); appMediaPlaybackController.previous(); }}
          >
            {rewindIcon.element}
          </Button>
          <Button
            class="btn-icon pinned-audio-ico"
            noRipple
            aria-label={I18n.format(playing() ? 'Pause' : 'Play', true)}
            onClick={(e) => { cancelEvent(e); appMediaPlaybackController.toggle(); }}
          >
            <div class="pinned-audio-play-icon" ref={playIconContainer} />
          </Button>
          <Button
            ref={nextEl}
            class="btn-icon"
            noRipple
            aria-label={I18n.format('KeyboardShortcuts.Action.NextMedia', true)}
            onClick={(e) => { cancelEvent(e); forwardIcon.play(); appMediaPlaybackController.next(); }}
          >
            {forwardIcon.element}
          </Button>
          <TopbarPlate.Content class={classNames('hover-effect', inert() && 'pinned-audio-content-inert')} ripple clickable disabled={inert()}>
            <TopbarPlate.Title>{title()}</TopbarPlate.Title>
            <TopbarPlate.Subtitle>
              <span class="pinned-audio-time">{timeText()}</span>
              {' • '}
              {subtitle()}
            </TopbarPlate.Subtitle>
          </TopbarPlate.Content>
          <div class="pinned-container-wrapper-utils pinned-audio-wrapper-utils">
            {volumeSelector.btn}
            {playbackRateButton.element}
            <Button.Icon
              ref={repeatEl}
              icon={repeatIcon()}
              noRipple
              aria-label={I18n.format('Schedule.Repeat', true)}
              onClick={(e) => {
                cancelEvent(e);
                const params = appMediaPlaybackController.getPlaybackParams();
                if(!params.round) {
                  appMediaPlaybackController.round = true;
                } else if(params.loop) {
                  appMediaPlaybackController.round = false;
                  appMediaPlaybackController.loop = false;
                } else {
                  appMediaPlaybackController.loop = !appMediaPlaybackController.loop;
                }
              }}
            />
            <TopbarPlate.CloseButton
              onClick={() => appMediaPlaybackController.stop(undefined, true)}
            />
          </div>
        </TopbarPlate.Body>
        <div class="pinned-audio-progress-wrapper">
          {progressLine.container}
        </div>
      </>
    )
  });

  // Click on the central content (title/subtitle) → open the source chat /
  // saved music tab. Clicks anywhere else on the plate do nothing.
  attachClickEvent(plate.container, (e) => {
    if(inert() || !findUpClassName(e.target, 'pinned-container-content')) {
      return;
    }

    const mid = +plate.container.dataset.mid;
    const peerId = plate.container.dataset.peerId.toPeerId();
    const savedMusicDocId = plate.container.dataset.savedMusicDocId;
    if(savedMusicDocId) {
      openSavedMusicTab(appSidebarRight, peerId);
      return;
    }

    const searchContext = appMediaPlaybackController.getSearchContext();
    appImManager.setInnerPeer({
      peerId,
      lastMsgId: mid,
      type: searchContext.isScheduled ? ChatType.Scheduled : undefined,
      threadId: searchContext.threadId
    });
  }, {listenerSetter});

  // ───────────────────────── State / event handlers ─────────────────────────

  function toggle(hide?: boolean): void {
    const current = !plate.container.classList.contains('is-visible');
    if((hide ??= !current) === current) return;

    SetTransition({
      element: plate.container,
      duration: 250,
      className: 'is-visible',
      forwards: !hide,
      onTransitionStart: () => {
        doubleRaf().then(() => {
          document.body.classList.toggle('is-pinned-audio-shown', !hide);
        });
      }
    });
  }

  const onPlaybackParams = (playbackParams: ReturnType<AppMediaPlaybackController['getPlaybackParams']>) => {
    playbackRateButton.setIcon();
    playbackRateButton.element.classList.toggle('active', playbackParams.playbackRate !== 1);
    setRepeatIcon(playbackParams.loop ? 'audio_repeat_single' : 'audio_repeat');
    repeatEl.classList.toggle('active', playbackParams.loop || playbackParams.round);
  };

  const onMediaPlay = ({doc, message, media, playbackParams, isSavedMusic, isLocal, isSlotted}: ReturnType<AppMediaPlaybackController['getPlayingDetails']>) => {
    let titleVal: JSX.Element, subtitleVal: JSX.Element;
    const isMusic = doc.type !== 'voice' && doc.type !== 'round';
    if(!isMusic) {
      titleVal = new PeerTitle({peerId: message.fromId, fromName: getFwdFromName(message.fwd_from)}).element;
      subtitleVal = formatFullSentTime(message.date);
    } else {
      const titles = getAudioTitles(doc);
      titleVal = wrapEmojiText(titles?.title);
      subtitleVal = titles?.performer ? wrapEmojiText(titles.performer) : i18n('AudioUnknownArtist');
    }

    // Slotted media (e.g. poll description / explanation audio) is played in
    // isolation — there's no next track to advance to, and looping/repeat
    // doesn't make sense for a one-off attachment. `previous` stays enabled
    // because `seekToStart` lets it act as a "restart" button.
    repeatEl.classList.toggle('hide', !isMusic || isSlotted);
    onPlaybackParams(playbackParams);
    volumeSelector.setMaxVolume(isMusic ? 1 : 2);
    volumeSelector.setGlobalVolume();

    progressLine.setMedia({media, duration: duration = doc.duration});
    toggleDisability([prevEl, nextEl], !message.peerId);
    // Visually mute the next button for slotted playback — there's no
    // next track to advance to. The click handler is a safe no-op on the
    // empty list loader, so we leave the button enabled.
    nextEl.classList.toggle('pinned-audio-no-next', isSlotted);

    plate.container.dataset.peerId = '' + message.peerId;
    plate.container.dataset.mid = '' + message.mid;
    if(isSavedMusic) {
      plate.container.dataset.savedMusicDocId = '' + doc.id;
    } else {
      delete plate.container.dataset.savedMusicDocId;
    }

    // The saved-music tab is a real destination even for a locally built message; anything else
    // local (the music picker) has none.
    setInert(!isSavedMusic && isLocal);

    setTitle(titleVal);

    // Solid is throwing an error when trying to set the subtitle as a fragment / array of nodes
    const subtitleSpan = document.createElement('span');
    subtitleSpan.append(subtitleVal);

    setSubtitle(subtitleSpan);

    followMedia(media);
    toggle(false);
  };

  const onPause = () => syncPlayIcon();
  const onStop = () => toggle(true);

  const toggleActivity = (active: boolean) => {
    apiManagerProxy.invokeVoid('toggleUninteruptableActivity', {
      activity: 'PlayingMedia',
      active
    });
  };

  listenerSetter.add(appMediaPlaybackController)('play', () => toggleActivity(true));
  listenerSetter.add(appMediaPlaybackController)('pause', () => toggleActivity(false));
  listenerSetter.add(appMediaPlaybackController)('stop', () => toggleActivity(false));
  listenerSetter.add(appMediaPlaybackController)('play', onMediaPlay);
  listenerSetter.add(appMediaPlaybackController)('pause', onPause);
  listenerSetter.add(appMediaPlaybackController)('stop', onStop);
  listenerSetter.add(appMediaPlaybackController)('playbackParams', onPlaybackParams);

  const playingDetails = appMediaPlaybackController.getPlayingDetails();
  if(playingDetails) {
    onMediaPlay(playingDetails);
    onPlaybackParams(playingDetails.playbackParams);
  }

  return {
    container: plate.container,
    destroy: () => {
      progressLine?.removeListeners();
      listenerSetter.removeAll();
      mediaListenerSetter.removeAll();
      plate.destroy();
    }
  };
}
